import { getAge } from "@instream-solutions/instream-design-system/lib/Utilities/shared";
import { States } from "./constants";
import {
  getCurrentMonth,
  getCurrentYear,
  MONTH_LIST,
} from "../../../common/shared";

export const makeExpensesPayload = (expenses, defaultInflation) => {
  return expenses.map((exp, index) => {
    return {
      id: exp.id ? exp.id : null,
      name: exp.name,
      amount: Number(exp.amount),
      appliesTo: exp.owner
        ? {
            id: exp.owner.id,
            name: `${exp.owner.firstName} ${exp.owner.lastName}`,
          }
        : {
            id: 0,
            name: "Household",
          },
      editIndex: index,
      hasEnding: false,
      inEdit: true,
      inflationRate: null,
      isEdited: true,
      isNew: true,
      rowType: "EXPENSE_ROW",
      scopeType: "PLAN",
      selectedTimestamp: Date.now(),
      startYear: exp.startYear,
      endYear: exp.endYear,
      startMonth: exp.startMonth,
      endMonth: exp.endMonth,
      sortOrder: 0,
    };
  });
};

export const makeIncomePayload = (incomes, inflationRate) => {
  return incomes.map((income, index) => {
    return {
      incomeId: income.id,
      incomeType: {
        key: income.incomeType.key,
        name: "Payroll",
      },
      amount: Number(income.amount),
      frequency: {
        key: "MONTHLY",
        name: "Monthly",
      },
      inflationRate: null,
      futureValue: false,
      appliesTo: income.owner
        ? {
            id: income.owner.id,
            name: `${income.owner.firstName} ${income.owner.lastName}`,
          }
        : {
            id: 0,
            name: "Household",
          },
      clientId: null,
      startYear: income.startYear,
      endYear: income.endYear,
      startMonth: income.startMonth,
      endMonth: income.endMonth,
      sortOrder: 0,
      interval: 1,
      isEdited: true,
      isIncludedInTaxGlidePath: false,
      name: income.name,
      notes: "",
      scopeType: "PLAN",
    };
  });
};

export const makeInsurancePayload = (insurances, clientId) => {
  return insurances.map((insurance) => {
    return {
      id: insurance.id,
      name: insurance.name,
      clientId: clientId,
      appliesTo: {
        id: insurance.appliesTo,
        name: "",
      },
      scopeType: insurance.scopeType,
      policyType: {
        key: insurance.policyType,
        name: "",
      },
      deathBenefit: insurance.deathBenefit,
      premium: insurance.premium,
      cashValue: insurance.cashValue,
      policyEndDate: insurance.policyEndDate,
      isEdited: insurance.isEdited,
      isCopied: insurance.isCopied,
    };
  });
};

export const makeAssetsPayload = (assets) => {
  return assets.map((item) => {
    return {
      ...item,
      preRetirementAllocation: item.preRetirementAllocation / 100,
      postRetirementAllocation: item.postRetirementAllocation / 100,
      contributions: [
        {
          id: item.contributionId,
          name: "",
          contributionType:
            item.contributionType === "monthly"
              ? { key: "AMOUNT", name: "USD" }
              : { key: "PERCENT", name: "% of Income" },
          amount: item.monthlyContribution,
          percentage: item.percentContribution / 100,
          frequencyType: { key: item.frequencyType },
          inflationRate: 0,
          futureValue: 0,
          startYear: item.startYear,
          endYear: item.endYear,
          startMonth: MONTH_LIST[item.startMonth - 1],
          endMonth: MONTH_LIST[item.endMonth - 1],
        },
      ],
    };
  });
};

export const checkDateRangeValid = (
  startYear,
  startMonth,
  endYear,
  endMonth
) => {
  const start = new Date(startYear, startMonth - 1, 1);
  const end = new Date(endYear, endMonth - 1, 1);
  return start <= end;
};

export const makeDefaultExpenses = (client, coClient) => {
  const clientBirthYear = parseInt(client.dateOfBirth.split("/")[2]);
  const clientBirthMonth = parseInt(client.dateOfBirth.split("/")[0]);
  const retirementYear = clientBirthYear + client.retirementAge;

  const firstToDie = getFirstClientToDie(client, coClient);
  const firstToDieBirthYear = parseInt(firstToDie.dateOfBirth.split("/")[2]);

  const firstToDieLifeExpectancyYear =
    firstToDieBirthYear + firstToDie.lifeExpectancyAge;

  const firstToDieBirthMonth = parseInt(firstToDie.dateOfBirth.split("/")[0]);

  const firstToDieDate = new Date(
    firstToDieLifeExpectancyYear,
    firstToDieBirthMonth,
    1
  );
  const firstToDieEndDate = new Date(
    firstToDieDate.setMonth(firstToDieDate.getMonth() + 10)
  );

  const lastToDie = getLongestLivingClient(client, coClient);
  const lastToDieBirthYear = parseInt(lastToDie.dateOfBirth.split("/")[2]);
  const lastToDieBirthMonth = parseInt(lastToDie.dateOfBirth.split("/")[0]);

  const lastToDieLifeExpectancyYear =
    lastToDieBirthYear + lastToDie.lifeExpectancyAge;

  const lastToDieDeathDate = new Date(
    lastToDieLifeExpectancyYear,
    lastToDieBirthMonth,
    1
  );

  const lastToDieDate = new Date(
    lastToDieDeathDate.setMonth(lastToDieDeathDate.getMonth() + 10)
  );

  const lastToDieMonth = lastToDieDate.getMonth() + 1;
  const lastToDieYear = lastToDieDate.getFullYear();

  const hasValidCoClient = hasCoClient(client, coClient);

  const owner = hasValidCoClient
    ? { id: 0, firstName: "Household", lastName: "" }
    : {
        id: client.id,
        firstName: client.firstName,
        lastName: client.lastName,
      };

  const hasRetired = retirementYear < getCurrentYear();

  const widowedPhaseStartYear =
    firstToDieEndDate.getMonth() + 1 === 12
      ? firstToDieEndDate.getFullYear() + 1
      : firstToDieEndDate.getFullYear();
  const widowedPhaseStartMonth =
    firstToDieEndDate.getMonth() + 1 === 12
      ? 1
      : firstToDieEndDate.getMonth() + 2;

  let widowedPhaseEndMonth = 0;

  if (widowedPhaseStartYear === lastToDieYear) {
    widowedPhaseEndMonth =
      widowedPhaseStartMonth > lastToDieMonth
        ? widowedPhaseStartMonth
        : lastToDieMonth;
  } else {
    widowedPhaseEndMonth = lastToDieMonth;
  }

  return [
    {
      name: "From now until Retirement",
      checked: false,
      owner: owner,
      amount: "0",
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: hasRetired ? getCurrentYear() : retirementYear,
      endMonth: hasRetired ? getCurrentMonth() + 1 : clientBirthMonth,
      isEdited: false,
    },
    {
      name: "During Retirement",
      checked: false,
      owner: owner,
      amount: "0",
      startYear: hasRetired
        ? getCurrentYear()
        : clientBirthMonth === 12
        ? retirementYear + 1
        : retirementYear,
      startMonth: hasRetired
        ? getCurrentMonth() + 1
        : clientBirthMonth === 12
        ? 1
        : clientBirthMonth + 1,
      endYear: firstToDieEndDate.getFullYear(),
      endMonth: firstToDieEndDate.getMonth() + 1,
      isEdited: false,
    },
    {
      name: "Widowed Phase",
      checked: false,
      owner: owner,
      amount: "0",
      startYear: widowedPhaseStartYear,
      startMonth: widowedPhaseStartMonth,
      endYear: lastToDieYear,
      endMonth: widowedPhaseEndMonth,
      isEdited: false,
    },
    {
      name: "Mortgage",
      checked: false,
      owner: owner,
      amount: "0",
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: lastToDieYear,
      endMonth: lastToDieMonth,
      isEdited: false,
    },
    {
      name: "Utilities",
      checked: false,
      owner: owner,
      amount: "0",
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: lastToDieYear,
      endMonth: lastToDieMonth,
      isEdited: false,
    },
    {
      name: "Groceries",
      checked: false,
      owner: owner,
      amount: "0",
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: lastToDieYear,
      endMonth: lastToDieMonth,
      isEdited: false,
    },
    {
      name: "Transportation",
      checked: false,
      owner: owner,
      amount: "0",
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: lastToDieYear,
      endMonth: lastToDieMonth,
      isEdited: false,
    },
    {
      name: "Entertainment",
      checked: false,
      owner: owner,
      amount: "0",
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: lastToDieYear,
      endMonth: lastToDieMonth,
      isEdited: false,
    },
    {
      name: "Personal Loan/Debt",
      checked: false,
      owner: owner,
      amount: "0",
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: lastToDieYear,
      endMonth: lastToDieMonth,
      isEdited: false,
    },
  ];
};

export const makeDefaultIncomes = (client, coClient) => {
  const clientBirthYear = parseInt(client.dateOfBirth.split("/")[2]);
  const clientBirthMonth = parseInt(client.dateOfBirth.split("/")[0]);
  const retirementYear = clientBirthYear + client.retirementAge;

  const firstToDie = getFirstClientToDie(client, coClient);
  const firstToDieBirthYear = parseInt(firstToDie.dateOfBirth.split("/")[2]);

  const firstToDieLifeExpectancyYear =
    firstToDieBirthYear + firstToDie.lifeExpectancyAge;

  const firstToDieBirthMonth = parseInt(firstToDie.dateOfBirth.split("/")[0]);

  const firstToDieDate = new Date(
    firstToDieLifeExpectancyYear,
    firstToDieBirthMonth,
    1
  );
  const firstToDieEndDate = new Date(
    firstToDieDate.setMonth(firstToDieDate.getMonth() + 10)
  );

  const lastToDie = getLongestLivingClient(client, coClient);
  const lastToDieBirthYear = parseInt(lastToDie.dateOfBirth.split("/")[2]);
  const lastToDieBirthMonth = parseInt(lastToDie.dateOfBirth.split("/")[0]);

  const lastToDieLifeExpectancyYear =
    lastToDieBirthYear + lastToDie.lifeExpectancyAge;

  const lastToDieDeathDate = new Date(
    lastToDieLifeExpectancyYear,
    lastToDieBirthMonth,
    1
  );

  const lastToDieDate = new Date(
    lastToDieDeathDate.setMonth(lastToDieDeathDate.getMonth() + 10)
  );

  const lastToDieMonth = lastToDieDate.getMonth() + 1;
  const lastToDieYear = lastToDieDate.getFullYear();

  const firstToRetireDate = getDefaultFirstToRetireDate(null, client, coClient);
  const lastToRetireDate = getDefaultLastToRetireDate(null, client, coClient);

  const hasValidCoClient = hasCoClient(client, coClient);
  const hasRetired = retirementYear < getCurrentYear();

  const widowedPhaseStartYear =
    firstToDieEndDate.getMonth() + 1 === 12
      ? firstToDieEndDate.getFullYear() + 1
      : firstToDieEndDate.getFullYear();
  const widowedPhaseStartMonth =
    firstToDieEndDate.getMonth() + 1 === 12
      ? 1
      : firstToDieEndDate.getMonth() + 2;

  let widowedPhaseEndMonth = 0;

  if (widowedPhaseStartYear === lastToDieYear) {
    widowedPhaseEndMonth =
      widowedPhaseStartMonth > lastToDieMonth
        ? widowedPhaseStartMonth
        : lastToDieMonth;
  } else {
    widowedPhaseEndMonth = lastToDieMonth;
  }

  if (!hasValidCoClient) {
    return [
      {
        name: "From now until Retirement",
        checked: false,
        owner: {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
        },
        amount: "0",
        incomeType: { key: "OTHER" },
        startYear: new Date().getFullYear(),
        startMonth: getCurrentMonth() + 1,
        endYear: hasRetired ? getCurrentYear() : retirementYear,
        endMonth: hasRetired ? getCurrentMonth() + 1 : clientBirthMonth,
        isEdited: false,
      },
      {
        name: "During Retirement",
        checked: false,
        owner: {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
        },
        amount: "0",
        incomeType: { key: "OTHER" },
        startYear: hasRetired
          ? getCurrentYear()
          : clientBirthMonth === 12
          ? retirementYear + 1
          : retirementYear,
        startMonth: hasRetired
          ? getCurrentMonth() + 1
          : clientBirthMonth === 12
          ? 1
          : clientBirthMonth + 1,
        endYear: firstToDieEndDate.getFullYear(),
        endMonth: firstToDieEndDate.getMonth() + 1,
        isEdited: false,
      },
      {
        name: "Widowed Phase",
        checked: false,
        owner: {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
        },
        amount: "0",
        incomeType: { key: "OTHER" },
        startYear: widowedPhaseStartYear,
        startMonth: widowedPhaseStartMonth,
        endYear: lastToDieYear,
        endMonth: widowedPhaseEndMonth,
        isEdited: false,
      },
      {
        name: "Payroll",
        checked: false,
        owner: {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
        },
        amount: "0",
        incomeType: { key: "PAYROLL" },
        startYear: new Date().getFullYear(),
        startMonth: getCurrentMonth() + 1,
        endYear: lastToRetireDate.getFullYear(),
        endMonth: lastToRetireDate.getMonth() + 1,
        isEdited: false,
      },
      {
        name: "Business",
        checked: false,
        owner: {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
        },
        amount: "0",
        incomeType: { key: "BUSINESS" },
        startYear: new Date().getFullYear(),
        startMonth: getCurrentMonth() + 1,
        endYear: lastToDieYear,
        endMonth: lastToDieMonth,
        isEdited: false,
      },
      {
        name: "Social Security",
        checked: false,
        owner: {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
        },
        amount: "0",
        incomeType: { key: "SOCIAL_SECURITY" },
        startYear: firstToRetireDate.getFullYear(),
        startMonth: firstToRetireDate.getMonth() + 1,
        endYear: lastToDieYear,
        endMonth: lastToDieMonth,
        isEdited: false,
      },
      {
        name: "Pension",
        checked: false,
        owner: {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
        },
        amount: "0",
        incomeType: { key: "PENSION" },
        startYear: firstToRetireDate.getFullYear(),
        startMonth: firstToRetireDate.getMonth() + 1,
        endYear: lastToDieYear,
        endMonth: lastToDieMonth,
        isEdited: false,
      },
      {
        name: "Other",
        checked: false,
        owner: {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
        },
        amount: "0",
        incomeType: { key: "OTHER" },
        startYear: new Date().getFullYear(),
        startMonth: getCurrentMonth() + 1,
        endYear: lastToDieYear,
        endMonth: lastToDieMonth,
        isEdited: false,
      },
    ];
  }

  const clientRetireDate = getRetireDate(client);
  const coClientRetireDate = getRetireDate(coClient);

  const clientLifeExpectancyDate = getLifeExpectancyDate(client);
  const coClientLifeExpectancyDate = getLifeExpectancyDate(coClient);

  return [
    {
      name: "From now until Retirement",
      checked: false,
      owner: { id: 0, firstName: "Household", lastName: "" },
      amount: "0",
      incomeType: { key: "OTHER" },
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: hasRetired ? getCurrentYear() : retirementYear,
      endMonth: hasRetired ? getCurrentMonth() + 1 : clientBirthMonth,
      isEdited: false,
    },
    {
      name: "During Retirement",
      checked: false,
      owner: { id: 0, firstName: "Household", lastName: "" },
      amount: "0",
      incomeType: { key: "OTHER" },
      startYear: hasRetired
        ? getCurrentYear()
        : clientBirthMonth === 12
        ? retirementYear + 1
        : retirementYear,
      startMonth: hasRetired
        ? getCurrentMonth() + 1
        : clientBirthMonth === 12
        ? 1
        : clientBirthMonth + 1,
      endYear: firstToDieEndDate.getFullYear(),
      endMonth: firstToDieEndDate.getMonth() + 1,
      isEdited: false,
    },
    {
      name: "Widowed Phase",
      checked: false,
      owner: { id: 0, firstName: "Household", lastName: "" },
      amount: "0",
      incomeType: { key: "OTHER" },
      startYear: widowedPhaseStartYear,
      startMonth: widowedPhaseStartMonth,
      endYear: lastToDieYear,
      endMonth: widowedPhaseEndMonth,
      isEdited: false,
    },
    {
      name: "Payroll",
      checked: false,
      owner: null,
      amount: "0",
      incomeType: { key: "PAYROLL" },
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: lastToRetireDate.getFullYear(),
      endMonth: lastToRetireDate.getMonth() + 1,
      isEdited: false,
    },
    {
      name: "Payroll",
      checked: false,
      owner: null,
      amount: "0",
      incomeType: { key: "PAYROLL" },
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: lastToRetireDate.getFullYear(),
      endMonth: lastToRetireDate.getMonth() + 1,
      isEdited: false,
    },
    {
      name: "Business",
      checked: false,
      owner: null,
      amount: "0",
      incomeType: { key: "BUSINESS" },
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: lastToDieYear,
      endMonth: lastToDieMonth,
      isEdited: false,
    },
    {
      name: "Social Security",
      checked: false,
      owner: client,
      amount: "0",
      incomeType: { key: "SOCIAL_SECURITY" },
      startYear: clientRetireDate.getFullYear(),
      startMonth: clientRetireDate.getMonth() + 1,
      endYear: clientLifeExpectancyDate.getFullYear(),
      endMonth: clientLifeExpectancyDate.getMonth() + 1,
      isEdited: false,
    },
    {
      name: "Social Security",
      checked: false,
      owner: coClient,
      amount: "0",
      incomeType: { key: "SOCIAL_SECURITY" },
      startYear: coClientRetireDate.getFullYear(),
      startMonth: coClientRetireDate.getMonth() + 1,
      endYear: coClientLifeExpectancyDate.getFullYear(),
      endMonth: coClientLifeExpectancyDate.getMonth() + 1,
      isEdited: false,
    },
    {
      name: "Pension",
      checked: false,
      owner: null,
      amount: "0",
      incomeType: { key: "PENSION" },
      startYear: firstToRetireDate.getFullYear(),
      startMonth: firstToRetireDate.getMonth() + 1,
      endYear: lastToDieYear,
      endMonth: lastToDieMonth,
      isEdited: false,
    },
    {
      name: "Pension",
      checked: false,
      owner: null,
      amount: "0",
      incomeType: { key: "PENSION" },
      startYear: firstToRetireDate.getFullYear(),
      startMonth: firstToRetireDate.getMonth() + 1,
      endYear: lastToDieYear,
      endMonth: lastToDieMonth,
      isEdited: false,
    },
    {
      name: "Other",
      checked: false,
      owner: null,
      amount: "0",
      incomeType: { key: "OTHER" },
      startYear: new Date().getFullYear(),
      startMonth: getCurrentMonth() + 1,
      endYear: lastToDieYear,
      endMonth: lastToDieMonth,
      isEdited: false,
    },
  ];
};

export const getLongestLivingClient = (client, coClient) => {
  if (coClient === null) return client;
  if (coClient.personId === null) return client;

  const clientBirthYear = parseInt(client.dateOfBirth.split("/")[2]);
  const clientBirthMonth = parseInt(client.dateOfBirth.split("/")[0]);

  const coClientBirthYear = parseInt(coClient.dateOfBirth.split("/")[2]);
  const coClientBirthMonth = parseInt(coClient.dateOfBirth.split("/")[0]);

  const clientLifeExpectancyDate = new Date(
    clientBirthYear + client.lifeExpectancyAge,
    clientBirthMonth,
    1
  );
  const coClientLifeExpectancyDate = new Date(
    coClientBirthYear + coClient.lifeExpectancyAge,
    coClientBirthMonth,
    1
  );

  if (clientLifeExpectancyDate >= coClientLifeExpectancyDate) return client;
  return coClient;
};

export const hasCoClient = (client, coClient) => {
  return coClient.personId !== null;
};

export const getFirstClientToDie = (client, coClient) => {
  if (coClient === null) return client;
  if (coClient.personId === null) return client;

  const clientBirthYear = parseInt(client.dateOfBirth.split("/")[2]);
  const clientBirthMonth = parseInt(client.dateOfBirth.split("/")[0]);

  const coClientBirthYear = parseInt(coClient.dateOfBirth.split("/")[2]);
  const coClientBirthMonth = parseInt(coClient.dateOfBirth.split("/")[0]);

  const clientLifeExpectancyDate = new Date(
    clientBirthYear + client.lifeExpectancyAge,
    clientBirthMonth,
    1
  );
  const coClientLifeExpectancyDate = new Date(
    coClientBirthYear + coClient.lifeExpectancyAge,
    coClientBirthMonth,
    1
  );

  if (clientLifeExpectancyDate <= coClientLifeExpectancyDate) return client;
  return coClient;
};

export const getFirstClientToRetire = (client, coClient) => {
  if (coClient === null) return client;
  if (coClient.personId === null) return client;

  const clientBirthYear = parseInt(client.dateOfBirth.split("/")[2]);
  const clientBirthMonth = parseInt(client.dateOfBirth.split("/")[0]);

  const coClientBirthYear = parseInt(coClient.dateOfBirth.split("/")[2]);
  const coClientBirthMonth = parseInt(coClient.dateOfBirth.split("/")[0]);

  const clientRetirementDate = new Date(
    clientBirthYear + client.retirementAge,
    clientBirthMonth,
    1
  );
  const coClientRetirementDate = new Date(
    coClientBirthYear + coClient.retirementAge,
    coClientBirthMonth,
    1
  );

  if (clientRetirementDate <= coClientRetirementDate) return client;
  return coClient;
};

export const getLastClientToRetire = (client, coClient) => {
  if (coClient === null) return client;
  if (coClient.personId === null) return client;

  const clientBirthYear = parseInt(client.dateOfBirth.split("/")[2]);
  const clientBirthMonth = parseInt(client.dateOfBirth.split("/")[0]);

  const coClientBirthYear = parseInt(coClient.dateOfBirth.split("/")[2]);
  const coClientBirthMonth = parseInt(coClient.dateOfBirth.split("/")[0]);

  const clientRetirementDate = new Date(
    clientBirthYear + client.retirementAge,
    clientBirthMonth,
    1
  );
  const coClientRetirementDate = new Date(
    coClientBirthYear + coClient.retirementAge,
    coClientBirthMonth,
    1
  );

  if (clientRetirementDate >= coClientRetirementDate) return client;
  return coClient;
};

export const getStateAbbreviation = (fullStateName) => {
  for (let key in States) {
    if (States[key] === fullStateName) return key;
  }

  return "";
};

export const createDefaultPayrollEntry = (client, coClient) => {
  const lastToRetireDate = getDefaultLastToRetireDate(null, client, coClient);

  return {
    name: "Payroll",
    checked: false,
    owner: { id: 0, firstName: "Household", lastName: "" },
    amount: "0",
    incomeType: { key: "PAYROLL" },
    startYear: new Date().getFullYear(),
    startMonth: getCurrentMonth() + 1,
    endYear: lastToRetireDate.getFullYear(),
    endMonth: lastToRetireDate.getMonth() + 1,
    isEdited: false,
  };
};

export const getDefaultLifeExpectancyDate = (ownerId, client, coClient) => {
  if (ownerId === null || ownerId === 0) {
    const lastToDie = getLongestLivingClient(client, coClient);
    const lastToDieBirthYear = parseInt(lastToDie.dateOfBirth.split("/")[2]);
    const lastToDieBirthMonth = parseInt(lastToDie.dateOfBirth.split("/")[0]);

    const lastToDieLifeExpectancyYear =
      lastToDieBirthYear + lastToDie.lifeExpectancyAge;

    const lastToDieDeathDate = new Date(
      lastToDieLifeExpectancyYear,
      lastToDieBirthMonth,
      1
    );

    return new Date(
      lastToDieDeathDate.setMonth(lastToDieDeathDate.getMonth() + 10)
    );
  }

  if (ownerId === client.id) {
    const clientDeathBirthYear = parseInt(client.dateOfBirth.split("/")[2]);
    const clientDeathBirthMonth = parseInt(client.dateOfBirth.split("/")[0]);

    const clientLifeExpectancyYear =
      clientDeathBirthYear + client.lifeExpectancyAge;

    const clientDeathDate = new Date(
      clientLifeExpectancyYear,
      clientDeathBirthMonth,
      1
    );

    return new Date(clientDeathDate.setMonth(clientDeathDate.getMonth() + 10));
  }

  const coClientDeathBirthYear = parseInt(coClient.dateOfBirth.split("/")[2]);
  const coClientDeathBirthMonth = parseInt(coClient.dateOfBirth.split("/")[0]);

  const coClientLifeExpectancyYear =
    coClientDeathBirthYear + coClient.lifeExpectancyAge;

  const coClientDeathDate = new Date(
    coClientLifeExpectancyYear,
    coClientDeathBirthMonth,
    1
  );

  return new Date(
    coClientDeathDate.setMonth(coClientDeathDate.getMonth() + 10)
  );
};

export const getRetireDate = (selectedClient, noClip) => {
  const today = new Date();

  const birthYear = parseInt(selectedClient.dateOfBirth.split("/")[2]);
  const birthMonth = parseInt(selectedClient.dateOfBirth.split("/")[0]);

  const retirementYear = birthYear + selectedClient.retirementAge;

  const retireDate = new Date(retirementYear, birthMonth - 1, 1);

  if (noClip) return retireDate;

  return retireDate > today ? retireDate : today;
};

export const getLifeExpectancyDate = (selectedClient) => {
  const birthYear = parseInt(selectedClient.dateOfBirth.split("/")[2]);
  const birthMonth = parseInt(selectedClient.dateOfBirth.split("/")[0]);

  const lifeExpectancyYear = birthYear + selectedClient.lifeExpectancyAge;

  const lifeExpectancyDate = new Date(lifeExpectancyYear, birthMonth, 1);

  return new Date(
    lifeExpectancyDate.setMonth(lifeExpectancyDate.getMonth() + 10)
  );
};

export const getDefaultFirstToRetireDate = (ownerId, client, coClient) => {
  if (ownerId === null || ownerId === 0) {
    const firstToRetire = getFirstClientToRetire(client, coClient);
    return getRetireDate(firstToRetire);
  }

  if (ownerId === client.id) {
    return getRetireDate(client);
  }

  return getRetireDate(coClient);
};

export const getDefaultLastToRetireDate = (ownerId, client, coClient) => {
  if (ownerId === null || ownerId === 0) {
    const lastToRetire = getLastClientToRetire(client, coClient);
    return getRetireDate(lastToRetire);
  }

  if (ownerId === client.id) {
    return getRetireDate(client);
  }

  return getRetireDate(coClient);
};

export const createTaxCreditObject = (defaultTaxCredits) => {
  const getTaxCreditType = (creditType, filingStatus) => {
    return defaultTaxCredits.find((item) => {
      return (
        item.taxCreditType === creditType &&
        item.taxFillingStatusType === filingStatus
      );
    });
  };

  return {
    elderlyOrDisabled: {
      SINGLE: getTaxCreditType("CREDITS_FOR_ELDERLY_OR_DISABLED", "SINGLE"),
      MARRIED_FILING_JOINTLY: getTaxCreditType(
        "CREDITS_FOR_ELDERLY_OR_DISABLED",
        "MARRIED_FILING_JOINTLY"
      ),
      MARRIED_FILING_SEPARATELY: getTaxCreditType(
        "CREDITS_FOR_ELDERLY_OR_DISABLED",
        "MARRIED_FILING_SEPARATELY"
      ),
      HEAD_OF_HOUSEHOLD: getTaxCreditType(
        "CREDITS_FOR_ELDERLY_OR_DISABLED",
        "HEAD_OF_HOUSEHOLD"
      ),
    },
    capitalGains: {
      SINGLE: getTaxCreditType(
        "CREDITS_FOR_TAX_ON_UNDISTRIBUTED_CAPITAL_GAINS",
        "SINGLE"
      ),
      MARRIED_FILING_JOINTLY: getTaxCreditType(
        "CREDITS_FOR_TAX_ON_UNDISTRIBUTED_CAPITAL_GAINS",
        "MARRIED_FILING_JOINTLY"
      ),
      MARRIED_FILING_SEPARATELY: getTaxCreditType(
        "CREDITS_FOR_TAX_ON_UNDISTRIBUTED_CAPITAL_GAINS",
        "MARRIED_FILING_SEPARATELY"
      ),
      HEAD_OF_HOUSEHOLD: getTaxCreditType(
        "CREDITS_FOR_TAX_ON_UNDISTRIBUTED_CAPITAL_GAINS",
        "HEAD_OF_HOUSEHOLD"
      ),
    },
    residentialEnergyI: {
      SINGLE: getTaxCreditType(
        "RESIDENTIAL_ENERGY_EFFICIENT_PROPERTY_CREDIT_PART_I",
        "SINGLE"
      ),
      MARRIED_FILING_JOINTLY: getTaxCreditType(
        "RESIDENTIAL_ENERGY_EFFICIENT_PROPERTY_CREDIT_PART_I",
        "MARRIED_FILING_JOINTLY"
      ),
      MARRIED_FILING_SEPARATELY: getTaxCreditType(
        "RESIDENTIAL_ENERGY_EFFICIENT_PROPERTY_CREDIT_PART_I",
        "MARRIED_FILING_SEPARATELY"
      ),
      HEAD_OF_HOUSEHOLD: getTaxCreditType(
        "RESIDENTIAL_ENERGY_EFFICIENT_PROPERTY_CREDIT_PART_I",
        "HEAD_OF_HOUSEHOLD"
      ),
    },
    residentialEnergyII: {
      SINGLE: getTaxCreditType(
        "RESIDENTIAL_ENERGY_EFFICIENT_PROPERTY_CREDIT_PART_II",
        "SINGLE"
      ),
      MARRIED_FILING_JOINTLY: getTaxCreditType(
        "RESIDENTIAL_ENERGY_EFFICIENT_PROPERTY_CREDIT_PART_II",
        "MARRIED_FILING_JOINTLY"
      ),
      MARRIED_FILING_SEPARATELY: getTaxCreditType(
        "RESIDENTIAL_ENERGY_EFFICIENT_PROPERTY_CREDIT_PART_II",
        "MARRIED_FILING_SEPARATELY"
      ),
      HEAD_OF_HOUSEHOLD: getTaxCreditType(
        "RESIDENTIAL_ENERGY_EFFICIENT_PROPERTY_CREDIT_PART_II",
        "HEAD_OF_HOUSEHOLD"
      ),
    },
  };
};
